import sortBy from 'lodash/sortBy';
import { useCallback, useMemo, useState, useTransition } from 'react';
import { shuffleArray } from '@/utils';
import { useSdkQuery, useWidgetContext } from '@/provider';
import { useControlledState } from '@/hooks/use-controlled-state';
import { QuizPlayerItem } from '@/components/quiz-player';
import {
  QuizController,
  QuizWidgetActivity,
  QuizWidgetSource,
  QuizWidgetAttempt,
  QuizWidgetQuestion,
} from '../types';
import { useQuizHistory } from './use-quiz-history';

export type QuizActivitySelector = (
  activities: QuizWidgetActivity[]
) => QuizWidgetActivity | null;

export type QuizHistoryFilter = (
  activities: QuizWidgetActivity[]
) => QuizWidgetActivity[];

export type UseQuizControllerProps = {
  reference: string;
  displayIndex?: number;
  displayRandom?: boolean;
  requireConfirm?: boolean;
  filterHistory?: QuizHistoryFilter;
  onActivitiesLoad?: (data: QuizWidgetActivity[]) => void;
  onActivitySelect?: (data: QuizWidgetActivity) => void;
  onQuizLoad?: (data: QuizWidgetSource | null) => void;
  onAttemptCreated?: (attempt: QuizWidgetAttempt) => void;
  onStart?: () => void;
  onCancel?: () => void;
  onDisplayQuestion?: (question: QuizWidgetQuestion, index: number) => void;
  onAnswer?: (
    question: QuizWidgetQuestion,
    answer: unknown,
    isCorrect?: boolean
  ) => void;
  onTimeUp?: (question: QuizWidgetQuestion) => void;
  onFinish?: () => void;
  onSubmitSuccess?: (data: QuizWidgetAttempt) => void;
  onSubmitError?: (error: unknown) => void;
};

export const ACTIVITY_SELECTOR = {
  withLessAttempt: (activities: QuizWidgetActivity[]) =>
    sortBy(activities, (i) => i.attempts.length)[0] || null,
};

const DEFAULT_HISTORY_FILTER: QuizHistoryFilter = (activities) => activities;

export function useQuizController(
  props: UseQuizControllerProps
): QuizController {
  const {
    reference,
    displayRandom = true,
    displayIndex,
    requireConfirm = false,
    filterHistory = DEFAULT_HISTORY_FILTER,
    onActivitiesLoad = () => {},
    onActivitySelect = () => {},
    onQuizLoad = () => {},
    onAttemptCreated = () => {},
    onStart = () => {},
    onCancel = () => {},
    onAnswer = () => {},
    onTimeUp = () => {},
    onFinish = () => {},
    onDisplayQuestion = () => {},
    onSubmitSuccess = () => {},
    onSubmitError = () => {},
  } = props;

  const { sdk } = useWidgetContext();

  const [currentActivityId, setCurrentActivityId] = useState<string | null>(
    null
  );

  // load activities by reference
  const activities = useSdkQuery(
    `activity-${reference}`,
    (sdk) =>
      sdk.activity.getByReference(reference, {
        type: 'quiz',
      }),
    {
      onSuccess: (data) => {
        onActivitiesLoad(data);
      },
      revalidateOnMount: true,
    }
  );

  // select the current activity
  const currentActivity = useMemo(
    () => activities.data?.find((i) => i.id === currentActivityId) || null,
    [activities, currentActivityId]
  );

  // initialize the attempt history tracker
  const history = useQuizHistory(activities.data || [], filterHistory);

  // load the quiz related to the activity
  const quiz = useSdkQuery(
    currentActivity ? `quiz-${currentActivity.id}` : null,
    async (sdk) => currentActivity && sdk.quiz.getById(currentActivity.id),
    {
      onSuccess: onQuizLoad,
    }
  );

  const loading = activities.loading || quiz.loading;

  // holds a reference to the questions to display as
  // the array might be randomized compared to the original activity)
  const displayItems = useMemo(() => {
    const questions = quiz.data?.questions || [];

    // randomize choices for multiple questions quiz
    const items = questions.map((i) => {
      if (i.type !== 'multiple_choice') {
        return i;
      }

      const shuffledChoices = shuffleArray(i.choices);
      const correctAnswer = i.choices[i.correctAnswerIndex];
      const correctAnswerIndex = shuffledChoices.findIndex(
        (i) => i === correctAnswer
      );

      return { ...i, choices: shuffledChoices, correctAnswerIndex };
    });

    // eventualy randomize questions
    return displayRandom ? shuffleArray(items) : items;
  }, [quiz.data, displayRandom]);

  // the current display index
  const [displayIndexValue, setDisplayIndex] = useControlledState({
    value: displayIndex,
    defaultValue: 0,
    onChange: (i) => {
      if (displayItems[i]) onDisplayQuestion(displayItems[i], i);
    },
  });

  // prepare the questions to work with the quiz player
  const questions = useMemo(
    () => prepareQuizQuestions(displayItems),
    [displayItems]
  );

  // internal states
  const [answers, setAnswers] = useState<
    Record<string, string | boolean | null>
  >({});
  const [attempt, setAttempt] = useState<string | null>(null);

  // transition used when creating/submitting the attempt
  const [isTransitionPending, startTransition] = useTransition();

  // array of all given answers
  const answersList = useMemo(
    () =>
      Object.keys(answers).map((questionId) => ({
        questionId,
        //TODO: we need to allow 'null' as answer value in the API
        answer: answers[questionId] as string | boolean,
      })),
    [answers]
  );

  const handleStart = useCallback(() => {
    // ensure the quiz has not started yet
    if (!currentActivity) return;

    onStart();

    // function to create an attempt
    const startAttemptAction = async () => {
      if (!currentActivity) return;

      const response = await sdk.attempt.create({
        activityId: currentActivity.id,
      });

      setAttempt(response.id);
      onAttemptCreated(response);
    };

    // create an attempt without blocking user interactions
    startTransition(() => {
      startAttemptAction();
    });
  }, [onAttemptCreated, onStart, sdk.attempt, currentActivity]);

  const handleAnswer = useCallback(
    (questionId: string, answer: unknown, isCorrect: boolean) => {
      if (!quiz.data) return;

      const quizItem = quiz.data.questions.find((i) => i.id === questionId);

      if (!quizItem) return;

      const answerValue =
        quizItem?.type === 'true_false' ? Boolean(answer) : String(answer);

      setAnswers((v) => ({ ...v, [questionId]: answerValue }));
      onAnswer(quizItem, answer, isCorrect);
    },
    [onAnswer, quiz]
  );

  const handleTimeUp = useCallback(
    (questionId: string) => {
      if (!quiz.data) return;

      const quizItem = quiz.data.questions.find((i) => i.id === questionId);

      if (quizItem) {
        setAnswers((v) => ({ ...v, [questionId]: null }));
        onTimeUp(quizItem);
      }
    },
    [onTimeUp, quiz]
  );

  const handleCancel = useCallback(() => {
    setAnswers({});

    if (attempt) {
      sdk.attempt.update(attempt, {
        transition: 'CANCEL',
      });
      setAttempt(null);
    }

    setCurrentActivityId(null);
    onCancel();
  }, [onCancel, attempt, sdk.attempt]);

  const handleSubmit = useCallback(
    (score: number, maxScore: number) => {
      const submitAttemptAction = async () => {
        if (!attempt || isTransitionPending) return;

        try {
          const response = await sdk.attempt.update(attempt, {
            transition: 'SUBMIT',
            payload: {
              output: { answers: answersList },
            },
          });

          onSubmitSuccess(response);
        } catch (e) {
          onSubmitError(e);
        }
      };

      startTransition(() => {
        submitAttemptAction();
      });

      onFinish();

      const scoreScaled = !score || !maxScore ? 0 : score / maxScore / 100;
      history.register(scoreScaled * 100);
    },
    [
      sdk.attempt,
      attempt,
      history,
      answersList,
      isTransitionPending,
      onFinish,
      onSubmitError,
      onSubmitSuccess,
    ]
  );

  const selectActivity = useCallback(
    (activityId: string) => {
      const item = activities.data?.find((i) => i.id === activityId);
      if (item) {
        setCurrentActivityId(item?.id);
        onActivitySelect(item);
      }
    },
    [onActivitySelect, activities]
  );

  return {
    // quiz player props
    questions,
    displayIndex: displayIndexValue,
    requireConfirm,
    validate: mockValidate,
    events: {
      onNavigateTo: setDisplayIndex,
      onStart: handleStart,
      onAnswer: handleAnswer,
      onCancel: handleCancel,
      onTimeUp: handleTimeUp,
      onFinish: handleSubmit,
    },
    // own props
    loading,
    data: activities.data || [],
    saving: isTransitionPending,
    activity: currentActivity,
    selectActivity,
    attempts: history.attempts,
    quiz: quiz.data,
  };
}

const prepareQuizQuestions = (
  items: QuizWidgetQuestion[]
): QuizPlayerItem[] => {
  return items.map((i) => {
    let choices: QuizPlayerItem['choices'] = [];
    let correctAnswer: unknown;

    if (i.type === 'multiple_choice') {
      choices = i.choices.map((v) => ({ value: v, label: v }));
      correctAnswer = i.choices[i.correctAnswerIndex];
    } else if (i.type === 'true_false') {
      choices = [
        { label: 'Vero', value: true },
        { label: 'False', value: false },
      ];
      correctAnswer = i.correctAnswer;
    }

    return {
      id: i.id,
      question: i.question,
      choices,
      correctAnswer,
    };
  });
};

const mockValidate = async () => {
  // here we shoud do answer validation server side if needed
  return true;
};
